import React, { type ComponentProps, type PropsWithChildren, useRef } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { BodyText, Icon, theme, useHovered, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderBagButton = (props: PropsWithChildren<Props>) => {
  const { accessibilityLabel, children, icon, iconSize = 20, isDisabled, onPress } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const ref = useRef(null);
  const isHovered = useHovered(ref);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isHighlighted = isHovered && !isDisabled;

  // ─── Styles ────────────────────────────────────────────────────────────────

  const hoverOverlayStyles = [
    styles.hoverOverlay,
    hoverOverlayWebStyles,
    isHighlighted ? styles.hoverOverlayVisible : undefined,
  ];
  const labelStyles = [styles.label, isDisabled ? styles.labelDisabled : undefined];

  const iconColor = isDisabled ? theme.colors.OPACITY.DARK_KALE.DARK : theme.colors.CHARCOAL;

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      ref={ref}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      activeOpacity={0.75}
      disabled={isDisabled}
      onPress={onPress}
      style={styles.button}
    >
      <View pointerEvents="none" style={hoverOverlayStyles} />

      {icon ? <Icon name={icon} color={iconColor} width={iconSize} height={iconSize} /> : null}

      <BodyText sizeMatch={['14']} style={labelStyles}>
        {children}
      </BodyText>
    </TouchableOpacity>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    height: theme.spacing['12'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['2'],
    borderColor: theme.colors.GRAY,
    borderWidth: 1,
    borderRadius: theme.radius['3xLarge'],
    paddingHorizontal: theme.spacing['6'],
  },
  hoverOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderRadius: theme.radius['3xLarge'],
    opacity: 0,
  },
  hoverOverlayVisible: {
    opacity: 1,
  },
  label: {
    color: theme.colors.CHARCOAL,
  },
  labelDisabled: {
    color: theme.colors.OPACITY.DARK_KALE.DARK,
  },
});

const hoverOverlayWebStyles = webOnlyStyles({
  transition: `opacity ${theme.transitions.base}ms ease-in-out`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  accessibilityLabel: string;
  icon?: ComponentProps<typeof Icon>['name'];
  iconSize?: number;
  isDisabled: boolean;
  onPress: () => void;
};
